import { memo, useMemo } from "react";
import Plot from "react-plotly.js";
import type { IrrData } from "./types";
import { getIrrValue } from "./utils";

/** Memoized rollover chart Plot - expensive, should not re-render on excludeInput keystroke. */
export const IRRRolloverChart = memo(function IRRRolloverChart({
  selectedRolloverRoot,
  irrApiData,
  sofrAdjusted,
}: {
  selectedRolloverRoot: string | null;
  irrApiData: IrrData;
  sofrAdjusted: boolean;
}) {
  const rootData = irrApiData[selectedRolloverRoot ?? ""];
  const spreadData = rootData?.data ?? {};
  const spreadOrder = rootData?.spread_order ?? [];
  const activeSpread = rootData?.active_spread ?? "";
  const activeIndex = spreadOrder.indexOf(activeSpread);

  const data = useMemo(
    () =>
      Object.entries(spreadData)
        .filter(([series]) => {
          const idx = spreadOrder.indexOf(series);
          return idx >= 0 && idx >= activeIndex;
        })
        .sort(([a], [b]) => spreadOrder.indexOf(a) - spreadOrder.indexOf(b))
        .map(([series, seriesData], index) => ({
          x: seriesData.map((p) => p.daysToExpiry),
          y: seriesData.map((p) =>
            getIrrValue(p as { irr: number; irr_sofr_adjusted?: number }, sofrAdjusted)
          ),
          connectgaps: true,
          opacity: index === 0 ? 1 : Math.max(0.9 ** index - 0.4, 0.1),
          line: { width: index === 0 ? 2.5 : 2, shape: "linear" as const },
          type: "scatter" as const,
          mode: "lines+markers" as const,
          name: series,
          text: seriesData.map((p) => `Days to Expiry: ${p.daysToExpiry}`),
        })),
    [spreadData, spreadOrder, activeIndex, sofrAdjusted]
  );
  const layout = useMemo(
    () => ({
      autosize: true,
      title: "Historical IRR Data Near Expiry",
      dragmode: "zoom" as const,
      uirevision: `${selectedRolloverRoot ?? "none"}-${sofrAdjusted}`,
      xaxis: { title: "Days to Expiry (DTE)", autorange: "reversed" as const },
      yaxis: {
        title: { text: "IRR", standoff: 10 },
        tickformat: ".2%",
        hoverformat: ".3%",
      },
    }),
    [selectedRolloverRoot, sofrAdjusted]
  );

  return (
    <Plot
      className="w-100 h-100"
      style={{ width: "100%", height: "100%" }}
      useResizeHandler
      data={data}
      layout={layout}
      config={{ displaylogo: false, responsive: true }}
    />
  );
});
